// hooks/useAuth.jsx - UNIFIED SOLUTION - Fixed for Backend Compatibility
import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react'
import { checkLocalStorageOnReload, cleanupCorruptedData } from '../utils/localStorageChecker'
import { clearAuthData } from '../utils/sessionUtils.js'
import { getUserPlaceholderImage } from '../utils/imageUtils'

// Create authentication context
const AuthContext = createContext(null)

// Authentication provider component
export const AuthProvider = ({ children }) => {
  // Core authentication state
  const [user, setUser] = useState(null)
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)
  const [userCounts, setUserCounts] = useState({
    notifications: 0,
    chats: 0,
    cartItems: 0,
    favorites: 0
  })

  // CRITICAL: Single initialization control
  const initialized = useRef(false)
  const mounted = useRef(true)
  const initInProgress = useRef(false)
  const lastRouteChangeTime = useRef(0)
  // API Base URL
  const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000'

  // PURE FUNCTION: Clear authentication data (no hooks)
  const clearAuthData = () => {

    const authKeys = [
      'access_token',
      'refresh_token',
      'user_data',
      'device_id',
      'verification_data',
      'verification_id',
      'intended_route'
    ]

    authKeys.forEach(key => {
      try {
        localStorage.removeItem(key)
      } catch (error) {
        console.warn(`Failed to remove ${key}:`, error)
      }
    })

    if (mounted.current) {
      setUser(null)
      setIsAuthenticated(false)
      setError(null)
      setUserCounts({ notifications: 0, chats: 0, cartItems: 0, favorites: 0 })
    }
  }

  // Global flags to prevent duplicate calls across component instances
  if (!window.__authValidationInProgress) {
    window.__authValidationInProgress = false
  }
  if (!window.__authInitializationInProgress) {
    window.__authInitializationInProgress = false
  }

  // PURE FUNCTION: Validate token with backend (no hooks)
  const validateToken = async (token) => {
    if (!token || !mounted.current) return null

    // Prevent duplicate calls with a global flag
    if (window.__authValidationInProgress) {
      return null
    }

    window.__authValidationInProgress = true

    try {
      // Check if user is in profile creation mode - skip token verification
      const currentPath = window.location.pathname
      const isProfileCreation = currentPath.includes('/auth/complete-profile/') ||
        currentPath.includes('/auth/create-password/') ||
        currentPath.includes('/auth/register/') ||
        currentPath.includes('/auth/google-register/') ||
        currentPath.includes('/auth/apple-register/') ||
        currentPath.includes('/auth/google-callback/')

      if (isProfileCreation) {
        // Return user data from localStorage without server verification
        const storedUserData = localStorage.getItem('user_data')
        if (storedUserData) {
          try {
            const userData = JSON.parse(storedUserData)
            if (userData && (userData.id || userData.user_id)) {
              return userData
            }
          } catch (error) {
            console.warn('⚠️ Failed to parse stored user data during profile creation')
          }
        }
        return null
      }

      // Check localStorage integrity before making API call
      const localStorageCheck = await checkLocalStorageOnReload()

      if (!localStorageCheck.isValid) {
        console.warn('⚠️ localStorage issues detected before token verification:', localStorageCheck.errors)

        // Clean up corrupted data
        if (localStorageCheck.corruptedKeys.length > 0) {
          cleanupCorruptedData(localStorageCheck)
        }

        // If critical keys are missing, we might need to re-authenticate
        if (localStorageCheck.missingKeys.includes('access_token') || localStorageCheck.missingKeys.includes('user_data')) {
          console.error('❌ Critical authentication data missing from localStorage')
          return null
        }
      } else {
      }

      const controller = new AbortController()
      const timeoutId = setTimeout(() => controller.abort(), 10000)
      // First verify the token with the backend
      const response = await fetch(`${API_BASE_URL}/api/v1/verify-token`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        signal: controller.signal
      })
      clearTimeout(timeoutId)

      if (response.status === 200) {
        const data = await response.json()

        // Check if token is valid
        if (data.status === 'success' && data.data?.is_valid) {
          // Extract unread counts from API response
          // IMPORTANT: Use unread_notifications_count_in_drawer for notifications (NOT messages count)
          const unreadNotificationsCountInDrawer = data.data?.unread_notifications_count_in_drawer ?? 0
          const unreadMessagesCountInDrawer = data.data?.unread_messages_count_in_drawer ?? 0
          var deviceKey = localStorage.getItem('device_id')
          if (deviceKey == null) {
            const deviceId = 'device_' + Math.random().toString(36).substr(2, 9) + Date.now().toString(36)
            localStorage.setItem('device_id', deviceId)
          }
          // Update notification badge from API response - using unread_notifications_count_in_drawer
          if (mounted.current) {
            setUserCounts(prev => ({
              ...prev,
              notifications: unreadNotificationsCountInDrawer // Using notifications count, NOT messages count
            }))
          }

          // Update chat badge from API response
          if (mounted.current) {
            setUserCounts(prev => ({
              ...prev,
              chats: unreadMessagesCountInDrawer
            }))
            // Sync with localStorage
            localStorage.setItem('chat_badge_count', unreadMessagesCountInDrawer.toString())
            // Dispatch event to notify Navbar
            const badgeUpdateEvent = new CustomEvent('chat-badge-update', {
              detail: { count: unreadMessagesCountInDrawer }
            })
            window.dispatchEvent(badgeUpdateEvent)
          }

          // Prefer user data from API if available, fallback to localStorage
          let userData = data.data?.user;
          const storedUserData = localStorage.getItem('user_data');

          if (!userData && storedUserData) {
            try {
              userData = JSON.parse(storedUserData);
            } catch (e) {
              console.warn('Failed to parse stored user data');
            }
          }

          if (userData) {
            // Update localStorage with fresh data from API
            localStorage.setItem('user_data', JSON.stringify(userData));

            if (userData.user_type != "CLINIC_MODERATOR" && data.data.profile_status == "incomplete") {
              window.location.href = '/auth/complete-profile/' + userData.user_type;
              return;
            }
            // Check if user data is valid
            if (userData && (userData.id || userData.user_id)) {
              setTimeout(() => {
                window.dispatchEvent(new CustomEvent('user-logged-in'));
              }, 1000);

              // Update notification counts from stored data (only if API didn't provide it)
              // API response takes priority, so only use stored data as fallback
              // IMPORTANT: Use unread_notification_count_in_drawer for notifications (NOT messages count)
              if ((data.data?.unread_notifications_count_in_drawer === undefined || data.data?.unread_notifications_count_in_drawer === null) &&
                (userData.unread_notification_count !== undefined || userData.unread_notification_count_in_drawer !== undefined)) {
                const notificationCount = userData.unread_notification_count || 0;
                const drawerNotificationCount = userData.unread_notification_count_in_drawer || 0;

                if (mounted.current) {
                  setUserCounts(prev => ({
                    ...prev,
                    notifications: drawerNotificationCount // Using unread_notification_count_in_drawer, NOT messages count
                  }));
                }
              }

              // Run localStorage check after successful token verification
              const postVerificationCheck = await checkLocalStorageOnReload()

              if (!postVerificationCheck.isValid) {
                console.warn('⚠️ localStorage issues detected after token verification:', postVerificationCheck.errors)

                // Clean up any corrupted data that might have been introduced
                if (postVerificationCheck.corruptedKeys.length > 0) {
                  cleanupCorruptedData(postVerificationCheck)
                }
              } else {
              }

              return userData
            } else {
              throw new Error('Invalid user data')
            }
          } else {
            throw new Error('No user data found')
          }
        } else {
          throw new Error('Token verification failed')
        }
      } else if (response.status === 401) {
        const refreshSuccess = await refreshToken()
        if (refreshSuccess) {
          // Retry validation with new token
          const newToken = localStorage.getItem('access_token')
          if (newToken) {
            return await validateToken(newToken)
          }
        }
        return null
      }

      console.warn('⚠️ Unexpected response:', response.status)
      return null

    } catch (error) {
      if (error.name === 'AbortError') {
        console.warn('⏱️ Token validation timed out')
      } else {
        console.error('❌ Token validation error:', error)
      }
      return null
    } finally {
      // Reset the global flag
      window.__authValidationInProgress = false
    }
  }

  // Fetch user profile gender and merge into user data (non-blocking)
  const fetchAndMergeProfileGender = async (currentUserData) => {
    if (!currentUserData || currentUserData.gender || currentUserData.sex) return;
    try {
      const token = localStorage.getItem('access_token');
      if (!token) return;
      const response = await fetch(`${API_BASE_URL}/api/v1/user-profile`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        }
      });
      if (response.ok) {
        const result = await response.json();
        const profileData = result.data || result;
        const gender = profileData.gender || profileData.sex || null;
        if (gender) {
          const updatedUser = { ...currentUserData, gender };
          if (mounted.current) {
            setUser(updatedUser);
            localStorage.setItem('user_data', JSON.stringify(updatedUser));
          }
        }
      }
    } catch (e) {
      // Silently fail - gender is not critical for auth
    }
  };

  // PURE FUNCTION: Refresh access token using refresh token
  const refreshToken = async () => {
    try {

      // Check localStorage integrity before refresh
      const localStorageCheck = await checkLocalStorageOnReload()

      if (!localStorageCheck.isValid) {
        console.warn('⚠️ localStorage issues detected before token refresh:', localStorageCheck.errors)

        // Clean up corrupted data
        if (localStorageCheck.corruptedKeys.length > 0) {
          cleanupCorruptedData(localStorageCheck)
        }
      } else {
      }

      const refreshTokenValue = localStorage.getItem('refresh_token')
      if (!refreshTokenValue) {
        console.warn('⚠️ No refresh token found, cannot refresh')
        // Don't clear auth data when no refresh token - this might be causing the logout
        // clearAuthData()
        return false
      }

      const response = await fetch(`${API_BASE_URL}/api/v1/refresh-token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          refresh_token: refreshTokenValue
        })
      })

      if (response.ok) {
        const result = await response.json()

        // Update tokens in localStorage
        if (result.data?.access_token) {
          localStorage.setItem('access_token', result.data.access_token)
        }

        if (result.data?.refresh_token) {
          localStorage.setItem('refresh_token', result.data.refresh_token)
        }

        // Run localStorage check after successful token refresh
        const postRefreshCheck = await checkLocalStorageOnReload()

        if (!postRefreshCheck.isValid) {
          console.warn('⚠️ localStorage issues detected after token refresh:', postRefreshCheck.errors)

          // Clean up any corrupted data that might have been introduced
          if (postRefreshCheck.corruptedKeys.length > 0) {
            cleanupCorruptedData(postRefreshCheck)
          }
        } else {
        }

        return true
      } else {
        console.warn('⚠️ Token refresh failed:', response.status)
        // Don't clear auth data on refresh failure - this might be causing the logout
        // clearAuthData()
        return false
      }
    } catch (error) {
      console.error('❌ Token refresh error:', error)
      // Don't clear auth data on refresh error - this might be causing the logout
      // clearAuthData()
      return false
    }
  }

  // PURE FUNCTION: Initialize authentication (no hooks)
  const initializeAuth = async (forceRefresh = false) => {
    // CRITICAL: Prevent multiple simultaneous initializations (unless forced)
    if (!forceRefresh && (initialized.current || initInProgress.current || !mounted.current)) {
      return
    }

    // Global flag to prevent duplicate initializations across component instances (unless forced)
    if (!forceRefresh && window.__authInitializationInProgress) {
      return
    }

    // Add timestamp for debugging
    const initStart = new Date().toISOString()

    initInProgress.current = true
    window.__authInitializationInProgress = true

    try {

      if (mounted.current) {
        // setIsLoading(true)
        setError(null)
      }

      const token = localStorage.getItem('access_token')
      const storedUserData = localStorage.getItem('user_data')


      if (!token) {
        if (mounted.current) {
          setUser(null)
          setIsAuthenticated(false)
          setError(null)
        }
        initialized.current = true
        return
      }

      // Validate stored user data structure
      let localUserData = null
      if (storedUserData && storedUserData !== 'undefined' && storedUserData !== 'null') {
        try {
          localUserData = JSON.parse(storedUserData)
          if (localUserData && (localUserData.id || localUserData.user_id)) {
          } else {
            localUserData = null
          }
        } catch (error) {
          localUserData = null
        }
      }

      // Validate token with backend
      const validatedUserData = await validateToken(token)

      if (validatedUserData && mounted.current) {
        setUser(validatedUserData)
        setIsAuthenticated(true)

        // Update localStorage with fresh data
        localStorage.setItem('user_data', JSON.stringify(validatedUserData))

        // Fetch gender from profile API (non-blocking)
        fetchAndMergeProfileGender(validatedUserData)

        // Update notification counts from API response
        // IMPORTANT: Use unread_notification_count_in_drawer for notifications (NOT messages count)
        if (validatedUserData.unread_notification_count !== undefined || validatedUserData.unread_notification_count_in_drawer !== undefined) {
          const notificationCount = validatedUserData.unread_notification_count || 0;
          const drawerNotificationCount = validatedUserData.unread_notification_count_in_drawer || 0;

          setUserCounts(prev => ({
            ...prev,
            notifications: drawerNotificationCount // Using unread_notification_count_in_drawer, NOT messages count
          }));
        }

        // Set demo counts for test user
        if (validatedUserData.email === 'test@example.com') {
          setUserCounts(prev => ({
            ...prev,
            notifications: 3,
            chats: 2,
            cartItems: 5,
            favorites: 8
          }))
        }

        initialized.current = true

      } else {
        // If we have valid local user data, use it temporarily
        if (localUserData && (localUserData.id || localUserData.user_id)) {
          if (mounted.current) {
            setUser(localUserData)
            setIsAuthenticated(true)
          }
        } else {
          if (mounted.current) {
            setUser(null)
            setIsAuthenticated(false)
          }
        }
        initialized.current = true
      }

    } catch (error) {
      if (mounted.current) {
        setError('Authentication initialization failed')
        // Try to use cached data if available
        const storedUserData = localStorage.getItem('user_data')
        if (storedUserData && storedUserData !== 'undefined' && storedUserData !== 'null') {
          try {
            const localUserData = JSON.parse(storedUserData)
            if (localUserData && (localUserData.id || localUserData.user_id)) {
              setUser(localUserData)
              setIsAuthenticated(true)
            }
          } catch (parseError) {
            console.error('❌ Error parsing cached user data:', parseError)
          }
        }
      }
      initialized.current = true
    } finally {
      if (mounted.current) {
        setIsLoading(false)
      }
      initInProgress.current = false
      window.__authInitializationInProgress = false
    }
  }

  // SINGLE useEffect for initialization - NO DEPENDENCIES
  useEffect(() => {
    mounted.current = true

    // Only initialize once
    if (!initialized.current && !initInProgress.current) {
      initializeAuth()
    }

    // Add event listener for OAuth authentication
    const handleAuthSuccess = (event) => {
      const { user, token } = event.detail
      oauthLogin(user, token)
    }

    // Add route change detection for auth re-validation
    const handleRouteChange = () => {
      const now = Date.now()
      // Prevent multiple calls within 300ms
      if (now - lastRouteChangeTime.current < 300) {
        return
      }
      lastRouteChangeTime.current = now

      // Reset initialization flags to allow re-validation
      initialized.current = false
      initInProgress.current = false
      window.__authInitializationInProgress = false

      // Re-initialize auth on route change
      if (mounted.current) {
        initializeAuth(true) // Force refresh on route change
      }
    }

    // Listen for navigation events
    const handlePopState = () => {
      handleRouteChange()
    }

    // Listen for programmatic navigation (pushState/replaceState)
    const originalPushState = history.pushState
    const originalReplaceState = history.replaceState

    // Helper to extract pathname from the URL arg (args[2]) if present
    const extractPathnameFromArgs = (args) => {
      try {
        const urlArg = args && args[2]
        if (!urlArg) return null
        // If urlArg is a full URL or relative path, create a URL object
        const parsed = new URL(urlArg, window.location.origin)
        return parsed.pathname
      } catch (err) {
        return null
      }
    }

    history.pushState = function (...args) {
      const prevPathname = window.location.pathname
      originalPushState.apply(history, args)
      // Only treat as a route-change when the pathname actually changes.
      const newPathname = extractPathnameFromArgs(args) || window.location.pathname
      if (newPathname !== prevPathname) {
        setTimeout(handleRouteChange, 100) // Small delay to ensure route change is complete
      } else {
        // If pathname is unchanged, it's likely a query/search-only update; skip heavy re-validation
      }
    }

    history.replaceState = function (...args) {
      const prevPathname = window.location.pathname
      originalReplaceState.apply(history, args)
      const newPathname = extractPathnameFromArgs(args) || window.location.pathname
      if (newPathname !== prevPathname) {
        setTimeout(handleRouteChange, 100) // Small delay to ensure route change is complete
      } else {
        // Skip re-validation for replaceState that only updates the query string
      }
    }

    // Set up periodic token validation (every 5 minutes)
    const validationInterval = setInterval(() => {
      if (mounted.current && isAuthenticated && user) {
        // Only validate if we have a token and user is authenticated
        const token = localStorage.getItem('access_token')
        if (token) {
          // Silent validation - don't force refresh, just check if token is still valid
          validateToken(token).then(validatedUserData => {
            if (validatedUserData && mounted.current) {
              // Update user data if it changed
              setUser(validatedUserData)
              localStorage.setItem('user_data', JSON.stringify(validatedUserData))
              fetchAndMergeProfileGender(validatedUserData)
            } else {
              // Don't logout automatically, just log the issue
            }
          }).catch(error => {
            console.warn('⚠️ Periodic validation error:', error)
          })
        }
      }
    }, 5 * 60 * 1000) // 5 minutes

    window.addEventListener('auth-success', handleAuthSuccess)
    window.addEventListener('popstate', handlePopState)

    return () => {
      mounted.current = false
      clearInterval(validationInterval)
      window.removeEventListener('auth-success', handleAuthSuccess)
      window.removeEventListener('popstate', handlePopState)

      // Restore original methods
      history.pushState = originalPushState
      history.replaceState = originalReplaceState
    }
  }, []) // EMPTY DEPENDENCY ARRAY

  // OAuth login function for social login
  const oauthLogin = async (userData, token) => {
    try {

      if (mounted.current) {
        setUser(userData)
        setIsAuthenticated(true)
        setError(null)
        fetchAndMergeProfileGender(userData)

        // Update notification counts from user data if available
        // IMPORTANT: Use unread_notification_count_in_drawer for notifications (NOT messages count)
        if (userData.unread_notification_count !== undefined || userData.unread_notification_count_in_drawer !== undefined) {
          const notificationCount = userData.unread_notification_count || 0;
          const drawerNotificationCount = userData.unread_notification_count_in_drawer || 0;

          setUserCounts(prev => ({
            ...prev,
            notifications: drawerNotificationCount // Using unread_notification_count_in_drawer, NOT messages count
          }));
        }
      }

      // Store authentication data
      localStorage.setItem('access_token', token)
      localStorage.setItem('user_data', JSON.stringify(userData))

      // Mark as initialized
      initialized.current = true

      // User logged in successfully
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('user-logged-in'));
      }, 1000);

      return {
        success: true,
        user: userData,
        message: 'OAuth login successful'
      }
    } catch (error) {
      console.error('💥 OAuth login error:', error)
      return {
        success: false,
        error: 'OAuth authentication failed'
      }
    }
  }

  // Login function
  const login = async (credentials) => {
    try {
      setError(null)
      setIsLoading(true)


      // FIXED: Use correct endpoint and match backend expectations
      const response = await fetch(`${API_BASE_URL}/api/v1/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          email: credentials.email || credentials.identifier,
          password: credentials.password,
          remember_me: credentials.remember_me || false
        })
      })

      const data = await response.json()
      if (response.ok && data && mounted.current) {

        // FIXED: Handle Laravel backend response structure
        let responseData = data
        if (data.status === 'success' && data.data) {
          responseData = data.data
        }

        const { access_token, refresh_token, user } = responseData

        // Store authentication data
        localStorage.setItem('access_token', access_token)
        if (refresh_token) {
          localStorage.setItem('refresh_token', refresh_token)
        }
        localStorage.setItem('user_data', JSON.stringify(user))

        setUser(user)
        setIsAuthenticated(true)
        fetchAndMergeProfileGender(user)

        // Update notification counts from user data if available
        // IMPORTANT: Use unread_notification_count_in_drawer for notifications (NOT messages count)
        if (user.unread_notification_count !== undefined || user.unread_notification_count_in_drawer !== undefined) {
          const notificationCount = user.unread_notification_count || 0;
          const drawerNotificationCount = user.unread_notification_count_in_drawer || 0;

          setUserCounts(prev => ({
            ...prev,
            notifications: drawerNotificationCount // Using unread_notification_count_in_drawer, NOT messages count
          }));
        }

        // Mark as initialized
        initialized.current = true

        // User logged in successfully
        setTimeout(() => {
          window.dispatchEvent(new CustomEvent('user-logged-in'));
        }, 1000);

        return {
          success: true,
          user: user,
          message: 'Login successful'
        }
      } else {
        console.error('❌ Login failed:', data)
        const errorMessage = data.message || 'Login failed'
        setError(errorMessage)
        return {
          success: false,
          error: errorMessage,
          errors: data.errors
        }
      }

    } catch (error) {
      console.error('💥 Login error:', error)
      const errorMessage = 'Network error occurred'
      if (mounted.current) {
        setError(errorMessage)
      }
      return {
        success: false,
        error: errorMessage
      }
    } finally {
      if (mounted.current) {
        setIsLoading(false)
      }
    }
  }

  // Logout function
  const logout = async (options = {}) => {
    const { skipBackendCall = false, redirectTo = null } = options

    try {

      if (mounted.current) {
        setIsLoading(true)
      }

      const token = localStorage.getItem('access_token')

      // Clear local data first
      clearAuthData()

      // Reset initialization state
      initialized.current = false
      initInProgress.current = false

      // Try to notify backend (non-blocking)
      if (!skipBackendCall && token) {
        try {
          // FIXED: Use correct logout endpoint
          await fetch(`${API_BASE_URL}/api/v1/logout`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          })
        } catch (error) {
          console.warn('⚠️ Backend logout failed:', error)
        }
      }

      if (redirectTo) {
        window.location.href = redirectTo
      }

      return { success: true, message: 'Logged out successfully' }

    } catch (error) {
      console.error('💥 Logout error:', error)
      return { success: false, error: error.message }
    } finally {
      if (mounted.current) {
        setIsLoading(false)
      }
    }
  }

  // Force logout (clear everything and redirect)
  const forceLogout = () => {
    return logout({ skipBackendCall: true, redirectTo: '/auth/login' })
  }

  // Utility functions
  const getUserType = () => {
    if (!user) return null;
    const userType = user.user_type || user.userType || user.type
    const typeMapping = {
      'PET_GUARDIAN': 'PET_GUARDIAN',
      'VETERINARIAN': 'VETERINARIAN',
      'SERVICE_PROVIDER': 'SERVICE_PROVIDER',
      'PET_SHOP_OWNER': 'SERVICE_PROVIDER', // Backend uses SERVICE_PROVIDER (legacy: PET_SHOP_OWNER maps to SERVICE_PROVIDER)
      'service_provider': 'SERVICE_PROVIDER', // Backend uses SERVICE_PROVIDER
      'pet_shop_owner': 'SERVICE_PROVIDER', // Backend uses SERVICE_PROVIDER (legacy: pet_shop_owner maps to SERVICE_PROVIDER)
      'CLINIC_MODERATOR': 'CLINIC_MODERATOR',
      'FIRM': 'FIRM',
      'firm': 'FIRM',
      'ADMIN': 'ADMIN',
      'DEV_ADMIN': 'DEV_ADMIN'
    }
    return typeMapping[userType] || userType // Return original if not in mapping
  }

  const getDisplayName = () => {
    if (!user) return 'User'
    if (user.first_name && user.last_name) {
      return `${user.first_name} ${user.last_name}`
    }
    if (user.first_name) return user.first_name
    return user.display_name || user.username || user.name || user.email?.split('@')[0] || 'User'
  }

  const getProfileImageUrl = () => {
    if (!user) return null
    if (user.email === 'test@example.com') {
      return getUserPlaceholderImage(user)
    }
    const imageUrl = user.profile_image_url || user.profileImageUrl || user.avatar || user.profile_image
    if (imageUrl) {
      if (imageUrl.startsWith('/storage/') || imageUrl.startsWith('/uploads/')) {
        return `${API_BASE_URL}${imageUrl}`
      }
      if (imageUrl.startsWith('http')) return imageUrl
      return `${API_BASE_URL}/${imageUrl}`
    }
    return null
  }

  const getInitials = () => {
    const displayName = getDisplayName()
    return displayName
      .split(' ')
      .map(name => name.charAt(0))
      .join('')
      .slice(0, 2)
      .toUpperCase()
  }

  const isDemoUser = () => {
    return user?.email === 'test@example.com'
  }

  const hasRole = (role) => {
    if (!user) return false
    return user.user_type === role || user.role === role
  }

  const clearError = () => {
    if (mounted.current) {
      setError(null)
    }
  }

  // Update user data (for posts count, etc.)
  const updateUserData = (updates) => {
    if (mounted.current && user) {
      const updatedUser = { ...user, ...updates }
      setUser(updatedUser)
      localStorage.setItem('user_data', JSON.stringify(updatedUser))

      // Auto-update notification counts if they're included in the updates
      // IMPORTANT: Use unread_notification_count_in_drawer for notifications (NOT messages count)
      if (updates.unread_notification_count !== undefined || updates.unread_notification_count_in_drawer !== undefined) {
        const notificationCount = updates.unread_notification_count || 0;
        const drawerNotificationCount = updates.unread_notification_count_in_drawer || 0;

        setUserCounts(prev => ({
          ...prev,
          notifications: drawerNotificationCount || notificationCount // Prefer unread_notification_count_in_drawer, NOT messages count
        }));
      }

    }
  }

  // Update posts count
  const updatePostsCount = (newCount) => {

    if (mounted.current && user) {
      // Update both user.posts_count (for API compatibility) and user.stats.posts (for UI display)
      const updatedUser = {
        ...user,
        posts_count: newCount,
        stats: {
          ...user.stats,
          posts: newCount
        }
      }
      setUser(updatedUser)
      localStorage.setItem('user_data', JSON.stringify(updatedUser))
    } else {
    }
  }

  // Update chat count
  const updateChatCount = (newCount) => {
    if (mounted.current) {
      setUserCounts(prev => ({
        ...prev,
        chats: newCount
      }))
    }
  }

  // Update notification count - memoized to prevent infinite loops
  const updateNotificationCount = useCallback((newCount) => {
    if (mounted.current) {
      setUserCounts(prev => {
        // Only update if the count actually changed
        if (prev.notifications === newCount) {
          return prev;
        }
        return {
          ...prev,
          notifications: newCount
        };
      });
    }
  }, []); // Empty dependency array since it only uses refs and setState

  // Update notification counts from API response
  const updateNotificationCountsFromAPI = (unreadCount, drawerCount) => {
    if (mounted.current) {
      const notificationCount = drawerCount || unreadCount || 0;
      setUserCounts(prev => ({
        ...prev,
        notifications: notificationCount
      }));
    }
  }

  // Update cart items count
  const updateCartCount = (newCount) => {
    if (mounted.current) {
      setUserCounts(prev => ({
        ...prev,
        cartItems: newCount
      }))
    }
  }

  // Update favorites count
  const updateFavoritesCount = (newCount) => {
    if (mounted.current) {
      setUserCounts(prev => ({
        ...prev,
        favorites: newCount
      }))
    }
  }

  // Debug function
  const debugAuth = () => {
    // Debugging disabled
  }

  // Manual refresh function for components to trigger auth re-validation
  const refreshAuth = async () => {
    initialized.current = false
    initInProgress.current = false
    window.__authInitializationInProgress = false
    await initializeAuth(true)
  }

  const contextValue = {
    // State
    user,
    userData: user, // Alias for compatibility
    isAuthenticated,
    isLoading,
    error,
    userCounts,

    // Authentication functions
    login,
    oauthLogin,
    logout,
    forceLogout,
    clearError,
    refreshToken,
    refreshAuth, // New manual refresh function

    // User data update functions
    updateUserData,
    updatePostsCount,
    updateChatCount,
    updateNotificationCount,
    updateNotificationCountsFromAPI,
    updateCartCount,
    updateFavoritesCount,

    // Utility functions
    getUserType,
    getDisplayName,
    getProfileImageUrl,
    getInitials,
    isDemoUser,
    hasRole,

    // Debug
    debugAuth
  }

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  )
}

// Custom hook to use authentication context
export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

// Alias hook for compatibility
export const useUserData = () => {
  return useAuth()
}

// HOC for protected routes
export const withAuth = (WrappedComponent, allowedRoles = []) => {
  return function AuthenticatedComponent(props) {
    const { isAuthenticated, isLoading, user, hasRole } = useAuth()

    if (isLoading) {
      return (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          fontFamily: '"Red Hat Display", sans-serif'
        }}>
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '16px'
          }}>
            <div style={{
              width: '40px',
              height: '40px',
              border: '4px solid #E5E7EB',
              borderTop: '4px solid #3B82F6',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }}></div>
            <p style={{ color: '#6B7280', fontSize: '14px' }}>Loading...</p>
          </div>
        </div>
      )
    }

    if (!isAuthenticated) {
      localStorage.setItem('intended_route', window.location.pathname)
      clearAuthData()
      window.location.href = '/auth/login'
      return null
    }

    if (allowedRoles.length > 0 && !allowedRoles.some(role => hasRole(role))) {
      return (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          fontFamily: '"Red Hat Display", sans-serif'
        }}>
          <div style={{ textAlign: 'center', maxWidth: '400px', padding: '40px' }}>
            <h2 style={{ fontSize: '24px', fontWeight: 600, color: '#EF4444', marginBottom: '16px' }}>
              Access Denied
            </h2>
            <p style={{ color: '#6B7280', marginBottom: '24px', lineHeight: '1.5' }}>
              You don't have permission to access this resource.
            </p>
            <button
              onClick={() => window.history.back()}
              style={{
                padding: '12px 24px',
                background: '#EF4444',
                color: '#FFFFFF',
                border: 'none',
                borderRadius: '8px',
                fontFamily: '"Red Hat Display", sans-serif',
                fontWeight: 600,
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              Go Back
            </button>
          </div>
        </div>
      )
    }

    return <WrappedComponent {...props} />
  }
}

export default useAuth